<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: 'Brand Profiles', to: '/settings/brand-profiles' }
    ]"
  >
    <template #right>
      <Button
        label="New Brand Profile"
        severity="primary"
        icon="plus"
        @click="newBrandProfile"
      />
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="BrandProfiles"
      :value="brandProfiles"
      :loading="isLoading"
      removable-sort
      sort-field="id"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-brand-profiles"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'name', 'orgName']"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
      </template>

      <Column
        field="id"
        header="ID"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.id }}</div>
        </template>
      </Column>

      <Column header="Logo">
        <template #body="{ data }">
          <div class="h-8 w-14 flex items-center">
            <Image
              v-if="data.logoUrl"
              :src="data.logoUrl"
              image-class="h-8 max-w-14"
            />
            <Icon
              v-else
              type="image-slash"
              class="text-secondary"
            />
          </div>
        </template>
      </Column>

      <Column
        field="name"
        header="Name"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.name }}</div>
        </template>
      </Column>

      <Column
        field="orgName"
        header="Owner"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip v-if="data.orgId">{{ data.orgName }}</Chip>
          <Chip
            v-else
            outlined
          >
            Global (manufacturer)
          </Chip>
        </template>
      </Column>

      <Column
        field="accentColor"
        header="Accent Colour"
      >
        <template #body="{ data }">
          <div
            v-if="data.accentColor"
            class="flex items-center gap-2"
          >
            <span
              class="inline-block h-4 w-4 rounded-full border border-[var(--border-color)]"
              :style="{ backgroundColor: data.accentColor }"
            ></span>
            {{ data.accentColor }}
          </div>
          <span
            v-else
            class="text-secondary"
          >
            -
          </span>
        </template>
      </Column>

      <Column
        field="isActive"
        header="Active"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.isActive"
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="success"
          >
            Active
          </Chip>
          <Chip
            v-else
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="danger"
          >
            Disabled
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="[
              { value: true, label: 'Active' },
              { value: false, label: 'Disabled' }
            ]"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column :sortable="false">
        <template #body="{ data }">
          <div class="flex">
            <Button
              v-tippy="$t('ui.actions.edit')"
              icon="pen"
              plain
              severity="secondary"
              @click="editBrandProfile(data)"
            ></Button>
            <Button
              v-tippy="$t('ui.actions.delete')"
              icon="trash"
              plain
              severity="danger"
              @click="deleteBrandProfile(data)"
            ></Button>
          </div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="brandProfileModalIsOpen"
      :title="brandProfileState.id ? 'Edit Brand Profile' : 'New Brand Profile'"
      icon="palette"
      :buttons="['ok', 'close']"
      close-text="Cancel"
      :ok-text="brandProfileState.id ? 'Save' : 'Create'"
      :show-loader-on-confirm="true"
      :is-confirming="brandProfileApiRequest.isLoading.value"
      :disable-close="brandProfileApiRequest.isLoading.value"
      min-width="600"
      overflow-behaviour="visible"
      @close-modal="brandProfileModalIsOpen = false"
      @ok-modal="submitBrandProfile()"
    >
      <div class="grid grid-cols-2 gap-x-6">
        <FormGroup
          v-if="brandProfileState.id"
          label="Active"
          class="col-span-2"
        >
          <ToggleSwitch v-model="brandProfileState.isActive" />
        </FormGroup>
        <FormGroup
          v-else
          label="Owner"
          class="col-span-2"
        >
          <Select
            v-model="brandProfileState.orgId"
            :options="orgOptions"
            mode="single"
            value-key="value"
            label-key="label"
            placeholder="Global (manufacturer profile)"
            class="w-full"
          ></Select>
          <div class="text-sm text-secondary mt-1">Leave empty for a global profile (used by manufacturers).</div>
        </FormGroup>
        <FormGroup
          :label="$t('ui.common.name')"
          :is-required="true"
        >
          <Input
            v-model="brandProfileState.name"
            :has-error="v$profile.name.$error"
          />
          <InputError :has-error="v$profile.name.$error">{{ v$profile.name.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup label="Accent Colour">
          <div class="flex items-center gap-2">
            <Input
              v-model="brandProfileState.accentColor"
              placeholder="#000000"
            />
            <span
              v-if="brandProfileState.accentColor"
              class="inline-block h-6 w-6 shrink-0 rounded-full border border-[var(--border-color)]"
              :style="{ backgroundColor: brandProfileState.accentColor }"
            ></span>
          </div>
        </FormGroup>
        <FormGroup label="UI Gradient 1">
          <Input
            v-model="brandProfileState.uiGradient1"
            placeholder="#000000"
          />
        </FormGroup>
        <FormGroup label="UI Gradient 2">
          <Input
            v-model="brandProfileState.uiGradient2"
            placeholder="#000000"
          />
        </FormGroup>
      </div>

      <div
        v-if="brandProfileState.id"
        class="mt-2"
      >
        <div class="font-medium mb-2">Logos</div>
        <div class="grid grid-cols-2 gap-x-6">
          <div class="border border-[var(--border-color)] rounded-md p-3">
            <div class="text-sm text-secondary mb-2">Logo (SVG)</div>
            <div class="h-12 flex items-center mb-2">
              <Image
                v-if="brandProfileState.logoUrl"
                :src="brandProfileState.logoUrl"
                image-class="h-10 max-w-24"
              />
              <span
                v-else
                class="text-sm text-secondary"
              >
                No logo uploaded
              </span>
            </div>
            <Button
              label="Upload SVG"
              severity="secondary"
              outlined
              size="sm"
              icon="cloud-arrow-up"
              :is-loading="logoIsUploading"
              @click="triggerLogoUpload('svg')"
            />
          </div>
          <div class="border border-[var(--border-color)] rounded-md p-3">
            <div class="text-sm text-secondary mb-2">Logo (PNG - used in emails)</div>
            <div class="h-12 flex items-center mb-2">
              <Image
                v-if="brandProfileState.logoPngUrl"
                :src="brandProfileState.logoPngUrl"
                image-class="h-10 max-w-24"
              />
              <span
                v-else
                class="text-sm text-secondary"
              >
                No logo uploaded
              </span>
            </div>
            <Button
              label="Upload PNG"
              severity="secondary"
              outlined
              size="sm"
              icon="cloud-arrow-up"
              :is-loading="logoIsUploading"
              @click="triggerLogoUpload('png')"
            />
          </div>
        </div>
        <input
          ref="logoFileInput"
          type="file"
          :accept="logoUploadType === 'svg' ? '.svg' : '.png'"
          style="display: none"
          @change="uploadLogo"
        />
      </div>
      <div
        v-else
        class="text-sm text-secondary mt-2"
      >
        Logos can be uploaded after the profile has been created.
      </div>
    </Modal>
  </teleport>
</template>

<script setup>
import { ref } from 'vue'

// components
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Modal from '@/components/modal/Modal.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import Chip from '@/components/chip/Chip.vue'
import Image from '@/components/image/Image.vue'
import Icon from '@/components/icon/Icon.vue'
import Select from '@/components/forms/Select.vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'
import Swal from 'sweetalert2'

// composables
import useApiRequest from '@/composables/useApiRequest'

// utilities
import { alertToast } from '@/utilities/notification'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const brandProfiles = ref(null)
const orgOptions = ref([])

const brandProfilesApiRequest = useApiRequest()
const orgsApiRequest = useApiRequest()
const brandProfileApiRequest = useApiRequest()
const deleteApiRequest = useApiRequest()
const logoUploadApiRequest = useApiRequest()
const isLoading = brandProfilesApiRequest.isLoading

const brandProfileModalIsOpen = ref(false)
const brandProfileState = ref({})

const logoFileInput = ref(null)
const logoUploadType = ref('svg')
const logoIsUploading = logoUploadApiRequest.isLoading

const filters = ref([])

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.IN },
    isActive: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  // remove from session storage
  sessionStorage.removeItem('dt-settings-brand-profiles')
  initFilters()
}

function fetchBrandProfiles() {
  brandProfilesApiRequest
    .send({
      endpoint: '/v1/brand-profiles',
      params: { showDisabled: true }
    })
    .then(response => {
      if (response.success) {
        brandProfiles.value = response.data
      } else {
        alertToast('Error', 'Failed to fetch brand profiles', 'error')
      }
    })
}
fetchBrandProfiles()

// Orgs feed the Owner select when creating a profile
orgsApiRequest
  .send({
    endpoint: '/v1/orgs',
    params: { showDisabled: true }
  })
  .then(response => {
    if (response.success) {
      orgOptions.value = response.data.map(org => ({ value: org.id, label: org.name }))
    }
  })

function newBrandProfile() {
  brandProfileState.value = {
    id: null,
    orgId: null,
    name: null,
    logoUrl: null,
    logoPngUrl: null,
    accentColor: null,
    uiGradient1: null,
    uiGradient2: null,
    isActive: true
  }
  brandProfileModalIsOpen.value = true
}

function editBrandProfile(profile) {
  brandProfileState.value = { ...profile }
  brandProfileModalIsOpen.value = true
}

const brandProfileRules = {
  name: { required }
}
const v$profile = useVuelidate(brandProfileRules, brandProfileState, { $stopPropagation: true })

async function submitBrandProfile() {
  const isFormCorrect = await v$profile.value.$validate()
  // Block submission until the form is valid
  if (!isFormCorrect) {
    return
  }

  const isNew = !brandProfileState.value.id
  const dataToPost = {
    name: brandProfileState.value.name,
    accentColor: brandProfileState.value.accentColor,
    uiGradient1: brandProfileState.value.uiGradient1,
    uiGradient2: brandProfileState.value.uiGradient2
  }
  // Owner is set at creation only; active toggle applies to existing profiles
  if (isNew) {
    dataToPost.orgId = brandProfileState.value.orgId
  } else {
    dataToPost.isActive = brandProfileState.value.isActive
  }

  try {
    const response = await brandProfileApiRequest.send({
      method: isNew ? 'POST' : 'PATCH',
      endpoint: isNew ? '/v1/brand-profiles' : `/v1/brand-profiles/${brandProfileState.value.id}`,
      data: JSON.stringify(dataToPost)
    })
    if (!response.success) {
      alertToast('Error', 'Failed to save brand profile', 'error')
      return
    }
    alertToast('Success!', 'Brand profile saved', 'success')

    if (isNew) {
      // Stay open in edit mode so logos can be uploaded straight away
      brandProfileState.value = { ...brandProfileState.value, ...response.data }
    } else {
      brandProfileModalIsOpen.value = false
    }
    fetchBrandProfiles()
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$profile.value.$reset()
}

async function deleteBrandProfile(profile) {
  const result = await Swal.fire({
    title: 'Delete brand profile?',
    text: `"${profile.name}" will be permanently deleted. Profiles still linked to a manufacturer, dealership or org cannot be deleted.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  })
  // Only proceed when the user confirms the deletion
  if (!result.isConfirmed) {
    return
  }

  try {
    const response = await deleteApiRequest.send({
      method: 'DELETE',
      endpoint: `/v1/brand-profiles/${profile.id}`
    })
    if (!response.success) {
      alertToast('Cannot delete', response.errors?.[0] ?? 'Failed to delete brand profile', 'error')
      return
    }
    alertToast('Deleted', 'Brand profile deleted', 'success')
    fetchBrandProfiles()
  } catch (err) {
    // A 409 rejection carries the API errors on the attached http response (profile still in use)
    const apiError = err.http?.data?.errors?.[0]
    alertToast('Cannot delete', apiError ?? err.message, 'error')
  }
}

function triggerLogoUpload(type) {
  logoUploadType.value = type
  logoFileInput.value.click()
}

async function uploadLogo(event) {
  const file = event.target.files[0]
  event.target.value = ''
  // Nothing selected in the file dialog
  if (!file) {
    return
  }

  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await logoUploadApiRequest.send({
      method: 'POST',
      endpoint: `/v1/brand-profiles/${brandProfileState.value.id}/logo`,
      data: formData
    })
    if (!response.success) {
      alertToast('Error', 'Failed to upload logo', 'error')
      return
    }
    alertToast('Success!', 'Logo uploaded', 'success')
    // API returns logoUrl (svg) or logoPngUrl (png) for the uploaded file
    if (response.data.logoUrl) {
      brandProfileState.value.logoUrl = response.data.logoUrl
    }
    if (response.data.logoPngUrl) {
      brandProfileState.value.logoPngUrl = response.data.logoPngUrl
    }
    fetchBrandProfiles()
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
}
</script>

<style></style>
